import { motion } from 'framer-motion'
import { Check, Zap, Users } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useWaitlist } from '@/context/WaitlistContext'
import { useAnimateOnScroll } from '@/hooks/useAnimateOnScroll'
import { Counter } from '@/components/ui/Counter'

export function Scarcity() {
  const { t } = useLanguage()
  const { spotsLeft } = useWaitlist()
  const { ref, isVisible } = useAnimateOnScroll({ threshold: 0.2 })

  const progressPercentage = ((500 - spotsLeft) / 500) * 100

  return (
    <section id="pricing" className="relative py-24 overflow-hidden">
      {/* Background with Gold Accent */}
      <div className="absolute inset-0 bg-midnight-950" />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(247, 208, 70, 0.2) 0%, transparent 60%)',
          filter: 'blur(100px)',
        }}
      />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto"
        >
          {/* Main Card */}
          <div className="glass-card p-8 md:p-12 relative overflow-hidden">
            {/* Animated Gold Border */}
            <div className="absolute inset-0 rounded-2xl">
              <div
                className="absolute inset-0 rounded-2xl"
                style={{
                  background: 'linear-gradient(90deg, #f7d046, #e8b923, #c99a13, #e8b923, #f7d046)',
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 3s linear infinite',
                  padding: '2px',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  maskComposite: 'xor',
                  WebkitMaskComposite: 'xor',
                }}
              />
            </div>

            <div className="relative z-10">
              {/* Header */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={isVisible ? { scale: 1 } : {}}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gold-500/10 border border-gold-500/30 rounded-full mb-6"
                >
                  <Zap className="w-4 h-4 text-gold-400" />
                  <span className="text-gold-400 font-medium text-sm">Limited Spots</span>
                </motion.div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-100 mb-4">
                  {t.scarcity.headline}
                </h2>
                <p className="text-lg text-slate-400">
                  {t.scarcity.subheadline}
                </p>
              </div>

              {/* Spots Counter */}
              <div className="bg-midnight-800/50 rounded-xl p-6 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-400" />
                    <span className="text-slate-400">Founding spots remaining</span>
                  </div>
                  <div className="text-2xl font-bold">
                    <Counter value={spotsLeft} className="text-gold-400" suffix=" / 500" />
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="h-3 bg-midnight-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={isVisible ? { width: `${progressPercentage}%` } : {}}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="h-full bg-gradient-to-r from-gold-500 to-gold-400 rounded-full relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_linear_infinite]" />
                  </motion.div>
                </div>
              </div>

              {/* Features List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.scarcity.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isVisible ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-gold-400" />
                    </div>
                    <span className="text-slate-300">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
